import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Archive, Check, ChevronLeft, ChevronRight, Download, Pencil, Plus, SlidersHorizontal, Upload, X } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  ASSESSMENT_QUESTION_TYPE_LABELS,
  ASSESSMENT_QUESTION_TYPES,
  QUESTION_DIFFICULTIES,
  QUESTION_DIFFICULTY_LABELS,
  type QuestionFacetsQuery,
  type QuestionView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { ColumnsMenuButton } from '../../../components/data-table/ColumnsMenu';
import { DataTable } from '../../../components/data-table/DataTable';
import { downloadCsv, exportFileName, exportValueOf, toCsv } from '../../../components/data-table/table-export';
import type { ActiveFilter } from '../../../components/data-table/TableToolbar';
import type { TableViewsConfig } from '../../../components/data-table/TableViews';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { useTablePreferences } from '../../../components/data-table/use-table-preferences';
import { SearchInput } from '../../../components/Input';
import { MenuGroup, MenuItem, Popover, usePopover } from '../../../components/Popover';
import { EmptyState, FilteredEmptyState, ForbiddenState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDate, formatNumber } from '../../../lib/format';
import { EMPTY_SELECTION, isSelected, selectionCount, type SelectionState } from '../../../lib/selection';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useUi, type Density } from '../../../stores/ui';
import { useVentures } from '../../organisation/api/organisation-api';
import { QUESTION_PAGE_SIZE, useArchiveQuestion, useQuestionFacets, useQuestions } from '../api/assessments-api';
import { AssessmentsHeader } from '../components/AssessmentsHeader';
import { FacetFilterButton } from '../components/FacetFilterButton';
import { QuestionEditor } from '../components/QuestionEditor';
import { QuestionImport } from '../components/QuestionImport';
import styles from '../components/Assessments.module.css';
import pageStyles from './QuestionBankPage.module.css';
import fit from '../../../components/FitPage.module.css';

/**
 * ASM-01 — the question bank (F9.01). Questions belong to a venture and everyone who
 * builds tests there can use them. Filters live in the address, so a colleague opening
 * the link sees the same list (06-qol §6). Redesign Group 6 (Question-bank.dc.html): one
 * card under the section header, the four filters always in view, each option counted.
 */

const SCREEN_ID = 'question-bank';
const MIN_QUERY = 2;
const DENSITY_LABELS: Readonly<Record<Density, string>> = { comfortable: 'Comfortable', compact: 'Compact' };
const DENSITIES: readonly Density[] = ['comfortable', 'compact'];
/** The address keys a saved view keeps — not the page, and not the open editor (Q28). */
const VIEW_KEYS = ['q', 'type', 'difficulty', 'tag', 'family'] as const;

export interface QuestionBankSearch {
  readonly q?: string;
  readonly type?: string;
  readonly difficulty?: string;
  readonly tag?: string;
  readonly family?: string;
  readonly page?: number;
  /** Opens the editor on arrival: a question id, or `new`. */
  readonly edit?: string;
}

export function QuestionBankPage(): ReactNode {
  const canView = useCanView('assessment_template');
  const canEdit = useCanEdit('assessment_template');
  const navigate = useNavigate();
  const search = useSearch({ from: '/app-layout/assessments/questions' }) as QuestionBankSearch;
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [archiving, setArchiving] = useState<{ readonly question: QuestionView; readonly anchor: AnchorRect } | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const ventures = useVentures();
  const archive = useArchiveQuestion();
  const show = useToasts((state) => state.show);

  const query = search.q ?? '';
  const page = search.page && search.page > 0 ? search.page : 1;
  const type = ASSESSMENT_QUESTION_TYPES.find((value) => value === search.type);
  const difficulty = QUESTION_DIFFICULTIES.find((value) => value === search.difficulty);
  const listFilters: QuestionFacetsQuery = {
    ...(query.trim().length >= MIN_QUERY ? { query: query.trim() } : {}),
    ...(type ? { type } : {}),
    ...(difficulty ? { difficulty } : {}),
    ...(search.tag ? { tag: search.tag } : {}),
    ...(search.family ? { roleFamily: search.family } : {}),
  };
  const questions = useQuestions({ page, ...listFilters });
  const facets = useQuestionFacets(listFilters);
  const ventureList = (ventures.data ?? []).map((venture) => ({ id: venture.id, name: venture.name }));
  const columns = questionColumns(search, ventureList.length > 1);
  const preferences = useTablePreferences(SCREEN_ID, columns);

  if (!canView) return <ForbiddenState resource="the question bank" contact="Your admin" />;

  const rows = questions.data?.rows ?? [];
  const total = questions.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / QUESTION_PAGE_SIZE));
  const editing = search.edit === 'new' ? null : rows.find((row) => row.id === search.edit) ?? null;
  const isEditorOpen = search.edit === 'new' || editing !== null;

  function setSearch(next: QuestionBankSearch): void {
    setSelection(EMPTY_SELECTION);
    void navigate({ to: '/assessments/questions', search: next, replace: true });
  }
  const without = (key: keyof QuestionBankSearch): QuestionBankSearch => {
    const { [key]: _removed, page: _page, ...rest } = search;
    return rest;
  };
  const views: TableViewsConfig = { screen: SCREEN_ID, filters: viewFilters(search), onApply: (next) => setSearch(viewFilters(next)) };

  const filters: ActiveFilter[] = [
    ...(type ? [{ id: 'type', label: ASSESSMENT_QUESTION_TYPE_LABELS[type], onRemove: () => setSearch(without('type')) }] : []),
    ...(difficulty ? [{ id: 'difficulty', label: QUESTION_DIFFICULTY_LABELS[difficulty], onRemove: () => setSearch(without('difficulty')) }] : []),
    ...(search.tag ? [{ id: 'tag', label: `Skill: ${search.tag}`, onRemove: () => setSearch(without('tag')) }] : []),
    ...(search.family ? [{ id: 'family', label: `Role family: ${search.family}`, onRemove: () => setSearch(without('family')) }] : []),
  ];

  // S32 (Q27): a question has no page of its own to link to, so the bank offers only the export.
  function exportSelected(): void {
    const picked = rows.filter((row) => isSelected(selection, row.id));
    const withValues = columns.filter((column) => picked.some((row) => exportValueOf(column, row) !== ''));
    downloadCsv(exportFileName('questions'), toCsv(withValues, picked));
    const selected = selectionCount(selection, total);
    show({
      tone: 'success',
      message: `${picked.length} ${picked.length === 1 ? 'row' : 'rows'} exported`,
      detail: selected !== undefined && selected > picked.length ? `The ${picked.length} loaded on screen — page to include the rest.` : undefined,
    });
  }

  // The row leaves the moment Archive is pressed; a refusal puts it back and says why (CLAUDE.md §5a).
  function confirmArchive(question: QuestionView): void {
    setArchiving(null);
    archive.mutate(
      { id: question.id, version: question.version },
      {
        onSuccess: () => show({ tone: 'success', message: 'Question archived', detail: question.testsUsing > 0 ? 'Tests that use it keep it. It no longer appears in the bank.' : undefined }),
        onError: (cause) => show({ tone: 'danger', message: "The question wasn't archived", detail: errorMessage(cause) }),
      },
    );
  }

  const newButton = canEdit ? (
    <Button variant="solid" icon={Plus} onClick={() => setSearch({ ...search, edit: 'new' })}>
      New question
    </Button>
  ) : undefined;
  const counts = facets.data?.counts;
  const filterCount = filters.length + (query === '' ? 0 : 1);
  const setFilter = (key: 'type' | 'difficulty' | 'tag' | 'family', value: string | undefined): void =>
    setSearch({ ...without(key), ...(value === undefined ? {} : { [key]: value }) });

  return (
    <div className={`${pageStyles.page} ${fit.fit}`}>
      <AssessmentsHeader current="bank" />
      <section className={`${pageStyles.card} ${fit.fill}`} aria-labelledby="question-bank-title">
        <div className={pageStyles.head}>
          <h2 id="question-bank-title" className={pageStyles.title}>
            Question bank
          </h2>
          {/* Counts are shown, not hinted, so nobody opens a screen to find out whether there is work in it. */}
          <span className={pageStyles.total}>{questions.data ? formatNumber(total) : EMPTY}</span>
          <span className={pageStyles.grow} />
          {canEdit && (
            <Button variant="outline" icon={Upload} onClick={() => setIsImportOpen(true)}>
              Import from Excel
            </Button>
          )}
          {newButton}
        </div>
        <p className={pageStyles.hint}>Editing a question saves a new version. Candidates who already answered keep the wording they saw.</p>

        <div className={pageStyles.toolbar}>
          <span className={pageStyles.search}>
            <SearchInput
              aria-label="Search questions"
              value={query}
              onChange={(event) => setSearch({ ...without('q'), ...(event.target.value === '' ? {} : { q: event.target.value }) })}
              onClear={() => setSearch(without('q'))}
              placeholder="Search"
              // `/` focuses the primary search on any list screen (06-qol §2).
              data-primary-search
            />
          </span>
          <FacetFilterButton
            label="Kind of answer"
            anyLabel="Any kind"
            anyCount={counts?.type.any}
            options={counts?.type.options ?? ASSESSMENT_QUESTION_TYPES.map((value) => ({ value, label: ASSESSMENT_QUESTION_TYPE_LABELS[value], count: undefined }))}
            value={type}
            onChange={(value) => setFilter('type', value)}
          />
          <FacetFilterButton
            label="Difficulty"
            anyLabel="Any difficulty"
            anyCount={counts?.difficulty.any}
            options={counts?.difficulty.options ?? QUESTION_DIFFICULTIES.map((value) => ({ value, label: QUESTION_DIFFICULTY_LABELS[value], count: undefined }))}
            value={difficulty}
            onChange={(value) => setFilter('difficulty', value)}
          />
          <FacetFilterButton
            label="Skill"
            anyLabel="Any skill"
            anyCount={counts?.tag.any}
            options={counts?.tag.options ?? []}
            value={search.tag}
            onChange={(value) => setFilter('tag', value)}
          />
          <FacetFilterButton
            label="Role family"
            anyLabel="Any role family"
            anyCount={counts?.roleFamily.any}
            options={counts?.roleFamily.options ?? []}
            value={search.family}
            onChange={(value) => setFilter('family', value)}
          />
          <span className={pageStyles.grow} />
          <DensityMenu />
          <ColumnsMenuButton columns={columns} preferences={preferences} views={views} />
        </div>

        {filters.length > 0 && (
          <div className={pageStyles.filters}>
            <span className={pageStyles.filterLabel}>Filters</span>
            {filters.map((filter) => (
              <span key={filter.id} className={pageStyles.chip}>
                {filter.label}
                <button type="button" aria-label={`Remove ${filter.label}`} onClick={filter.onRemove}>
                  <X size={13} aria-hidden />
                </button>
              </span>
            ))}
            <button type="button" className={pageStyles.clearAll} onClick={() => setSearch({})}>
              Clear all
            </button>
          </div>
        )}

        <DataTable
          screenId={SCREEN_ID}
          label="Question bank"
          columns={columns}
          rows={rows}
          preferences={preferences}
          isColumnMenuInHeader={false}
          getRowId={(row) => row.id}
          total={total}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          onSelectionChange={setSelection}
          onOpenRow={(row) => setSearch({ ...search, edit: row.id })}
          exportName="questions"
          views={views}
          bulkActions={
            <Button size="sm" variant="ghost" icon={Download} onClick={exportSelected}>
              Export selected
            </Button>
          }
          rowActions={
            canEdit
              ? (row) => (
                  <>
                    <Button variant="quiet" size="sm" icon={Pencil} onClick={() => setSearch({ ...search, edit: row.id })}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" icon={Archive} aria-label={`Archive ${row.questionText}`} onClick={(event: MouseEvent<HTMLButtonElement>) => setArchiving({ question: row, anchor: anchorFrom(event.currentTarget) })} />
                  </>
                )
              : undefined
          }
          isLoading={questions.isPending}
          emptyState={
            filterCount > 0 ? (
              <FilteredEmptyState filterCount={filterCount} onClearFilters={() => setSearch({})} isInset />
            ) : (
              <EmptyState isInset title="No questions yet" body="Add the questions your tests will draw from. Multiple choice is marked by the system as soon as a test is submitted." action={newButton} />
            )
          }
        />

        {pageCount > 1 && (
          <nav className={pageStyles.pager} aria-label="Pages">
            <Button variant="ghost" size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => setSearch({ ...search, page: page - 1 })}>
              Previous
            </Button>
            <span className={pageStyles.pageOf}>
              Page {page} of {pageCount}
            </span>
            <Button variant="ghost" size="sm" disabled={page >= pageCount} onClick={() => setSearch({ ...search, page: page + 1 })}>
              Next
              <ChevronRight size={16} aria-hidden />
            </Button>
          </nav>
        )}
      </section>

      <Popover anchor={archiving?.anchor ?? null} onClose={() => setArchiving(null)} align="right" label="Archive question">
        {archiving && (
          <div className={styles.confirm}>
            <p className={styles.confirmTitle}>Archive this question?</p>
            <p className={styles.confirmBody}>
              {archiving.question.testsUsing > 0
                ? `${archiving.question.testsUsing} test${archiving.question.testsUsing === 1 ? '' : 's'} use it and keep it. It stops appearing in the bank.`
                : 'It stops appearing in the bank.'}
            </p>
            <div className={styles.confirmActions}>
              <Button variant="ghost" size="sm" onClick={() => setArchiving(null)}>
                Keep it
              </Button>
              <Button variant="danger" size="sm" onClick={() => confirmArchive(archiving.question)}>
                Archive
              </Button>
            </div>
          </div>
        )}
      </Popover>

      <QuestionEditor
        isOpen={isEditorOpen}
        onClose={() => setSearch(without('edit'))}
        question={editing}
        ventures={ventureList}
        defaultVentureId={ventureList[0]?.id ?? ''}
      />
      <QuestionImport isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} ventures={ventureList} defaultVentureId={ventureList[0]?.id ?? ''} />
    </div>
  );
}

/** The table's columns, in the mockup's order; Venture only for someone who works in more than one. */
function questionColumns(search: QuestionBankSearch, showVenture: boolean): readonly Column<QuestionView>[] {
  return [
    {
      id: 'question',
      header: 'Question',
      isIdentity: true,
      defaultWidth: 380,
      cell: (row) => (
        <span className={styles.questionCell} title={row.questionText}>
          <Link to="/assessments/questions" search={{ ...search, edit: row.id }} replace className={`${styles.questionText} ${styles.questionLink}`}>
            {row.questionText}
          </Link>
          {row.tags.length > 0 && <span className={styles.meta}>{row.tags.join(' · ')}</span>}
        </span>
      ),
      exportValue: (row) => row.questionText,
    },
    { id: 'type', header: 'Kind', defaultWidth: 200, cell: (row) => ASSESSMENT_QUESTION_TYPE_LABELS[row.type] },
    { id: 'points', header: 'Marks', isNumeric: true, defaultWidth: 80, cell: (row) => row.points },
    {
      id: 'marking',
      header: 'Marked by',
      defaultWidth: 130,
      cell: (row) => (row.isAutoMarked ? <Chip tone="success" plain>System</Chip> : <Chip tone="neutral" plain>A person</Chip>),
      exportValue: (row) => (row.isAutoMarked ? 'System' : 'A person'),
    },
    { id: 'difficulty', header: 'Difficulty', defaultWidth: 110, cell: (row) => (row.difficulty ? QUESTION_DIFFICULTY_LABELS[row.difficulty] : null) },
    { id: 'family', header: 'Role family', defaultWidth: 140, cell: (row) => row.roleFamily },
    { id: 'tests', header: 'In tests', isNumeric: true, defaultWidth: 90, cell: (row) => row.testsUsing },
    ...(showVenture ? [{ id: 'venture', header: 'Venture', defaultWidth: 140, cell: (row: QuestionView) => row.ventureName }] : []),
    {
      id: 'updated',
      header: 'Updated',
      defaultWidth: 150,
      cell: (row) => <span title={row.updatedByName ?? undefined}>{formatDate(row.updatedAt)} · v{row.versionNumber}</span>,
      exportValue: (row) => `${formatDate(row.updatedAt)} · v${row.versionNumber}`,
    },
  ];
}

/** The filter keys of the address, or of a saved view, as plain strings. */
function viewFilters(source: Readonly<Partial<Record<(typeof VIEW_KEYS)[number], unknown>>>): Record<string, string> {
  const filters: Record<string, string> = {};
  for (const key of VIEW_KEYS) {
    const value = source[key];
    if (typeof value === 'string' && value !== '') filters[key] = value;
  }
  return filters;
}

/** The mockup's View menu: only the density here, since the filters are in the toolbar. */
function DensityMenu(): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const density = useUi((state) => state.density);
  const setDensity = useUi((state) => state.setDensity);
  return (
    <>
      <Button ref={triggerRef} variant="quiet" size="sm" icon={SlidersHorizontal} onClick={toggle} aria-haspopup="menu" aria-expanded={isOpen}>
        View
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" label="View options">
        <MenuGroup label="Density">
          {DENSITIES.map((value) => (
            <MenuItem key={value} isSelected={density === value} onClick={() => setDensity(value)} meta={density === value ? <Check size={14} aria-hidden /> : undefined}>
              {DENSITY_LABELS[value]}
            </MenuItem>
          ))}
        </MenuGroup>
      </Popover>
    </>
  );
}
